/* Reconciliation: the definitions this tenant has, and how each one's last run went. D30/D31.
 *
 * The design's board: a header carrying the counts and the one control, search and three filter pills,
 * then the definitions as one card — what each compares, how its last run went, what that run found,
 * and when it runs next — each row a link into its own runs.
 *
 * **The masking statement is always on the page.** Not because the platform is proud of it — because a
 * table headed "Key (masked)" two clicks away is otherwise ambiguous between "we hid it from you" and
 * "we never had it". The join runs inside the customer's database with masking in the SELECT list
 * (hard rule 4), so nothing on this screen or any screen below it *can* recover a key. The design puts
 * the claim in the lede; the full sentence stays one click away under it.
 *
 * **The approver sees a sentence, not an error.** That role holds no reconciliation permission at
 * all, so every read here 403s — which is consistent rather than an oversight: every read it holds
 * pairs with an action it takes, and it takes no reconciliation action. Saying so is the difference
 * between a surface that belongs to somebody else and a surface that looks broken.
 *
 * **Three columns need data the list response does not carry, and all three are bounded and
 * disclosed.** `tolerance_version` is null in list responses by contract, a run's state lives on a
 * different route, and so does the schedule — so each needs a per-definition read, capped by
 * `MAX_DETAIL_LOOKUPS`, past which the cells read "not read" rather than a figure nothing measured.
 */

import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useQueries } from '@tanstack/react-query';

import { useConnections } from '@/entities/connection';
import {
  can,
  CREATE_RECONCILIATION_PERMISSION,
  READ_RECONCILIATION_PERMISSION,
  useIdentity,
} from '@/entities/session';
import { DatasetKindIcon, useDatasetNames } from '@/features/catalog-browser';
import type { Run } from '@/shared/api/generated/reconciliation';
import { ageText } from '@/shared/model/age';
import { compactNumber } from '@/shared/model/number';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { DataTable } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { PageHeader } from '@/shared/ui/PageHeader';
import { SegmentedFilter } from '@/shared/ui/SegmentedFilter';
import { SkeletonRows } from '@/shared/ui/Skeleton';

import { runsQuery, useDefinitions } from '../api/reconciliation';
import { runScheduleQuery, type ScheduleReading } from '../api/governance';
import { isConfigured, scheduleBadge } from '../model/governance';
import { definitionPath } from '../model/paths';
import {
  BOARD_FILTERS,
  breakTotalOf,
  matchesBoardFilter,
  matchesSearch,
  type BoardFilter,
} from '../model/reconciliation';
import { NewDefinitionWizard } from './NewDefinitionWizard';
import { RunStateTag } from './RunStateTag';

const MAX_DETAIL_LOOKUPS = 12;

/* The tolerance version in force, as one line under the definition's name, with the kind mix on
 * hover. Read from the per-definition route, the only one carrying a version. */
function LastRunCell({
  runs,
}: {
  readonly runs: readonly Run[] | undefined;
}): ReactElement {
  const { t } = useTranslation();

  if (runs === undefined) {
    return <span className="lq-muted">{t('reconciliation.notRead')}</span>;
  }
  const last = runs[0];
  if (last === undefined) {
    return <span className="lq-muted">{t('reconciliation.neverRun')}</span>;
  }

  return (
    <>
      <RunStateTag state={last.state} />
      <div className="lq-rcb__sub">
        {ageText(t, last.requested_at, new Date()) ?? last.requested_at}
      </div>
    </>
  );
}

/* What the last run found. A dash rather than a zero for a run that measured nothing — a refused or
 * failed run did not find zero breaks, it found nothing — and the zero in the healthy tone, because
 * a reconciliation that matched every row is the result somebody wants to see at a glance. */
function BreaksCell({
  runs,
}: {
  readonly runs: readonly Run[] | undefined;
}): ReactElement {
  const { t } = useTranslation();
  const last = runs?.[0];
  const total = last === undefined ? null : breakTotalOf(last);
  if (total === null) {
    return <span className="lq-muted">{t('reconciliation.noFigures')}</span>;
  }
  return (
    <span className="lq-rcb__breaks" data-tone={total > 0 ? 'error' : undefined}>
      {t('reconciliation.breaksCount', { count: total, figure: compactNumber(total) })}
    </span>
  );
}

/* When the definition runs by itself. "On request" is a stated choice rather than a blank, and a
 * paused or suspended schedule says so before its cadence — a cadence that is not running is the one
 * reading of this column that must not look like the others. */
function ScheduleCell({
  reading,
}: {
  readonly reading: ScheduleReading | undefined;
}): ReactElement {
  const { t } = useTranslation();
  if (reading === undefined) {
    return <span className="lq-muted">{t('reconciliation.notRead')}</span>;
  }
  if (!isConfigured(reading)) {
    return <span className="lq-muted">{t('reconciliation.schedule.cell.none')}</span>;
  }
  const badge = scheduleBadge(reading);
  return (
    <span className="lq-rcb__sched" data-state={badge}>
      {badge === 'enabled'
        ? reading.describes
        : t(`reconciliation.schedule.cell.${badge}`, { describes: reading.describes })}
    </span>
  );
}

export function ReconciliationBoard(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const identity = useIdentity();
  const definitions = useDefinitions();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<BoardFilter>('all');
  const [wizardOpen, setWizardOpen] = useState(false);

  const mayRead = can(identity.data, READ_RECONCILIATION_PERMISSION);
  const mayAuthor = can(identity.data, CREATE_RECONCILIATION_PERMISSION);

  const all = definitions.isSuccess ? definitions.data.items : [];
  const looked = all.slice(0, MAX_DETAIL_LOOKUPS);

  const runReads = useQueries({
    queries: looked.map((definition) => runsQuery(definition.id)),
  });
  const scheduleReads = useQueries({
    queries: looked.map((definition) => runScheduleQuery(definition.id)),
  });

  /* Only ever used to populate the wizard's two pickers and to name a definition's connections. */
  const connections = useConnections();
  const datasetNames = useDatasetNames();
  const connectionName = new Map(
    (connections.data?.items ?? []).map((connection) => [connection.id, connection.name]),
  );

  const runsById = new Map<string, readonly Run[]>();
  const scheduleById = new Map<string, ScheduleReading>();
  looked.forEach((definition, index) => {
    const runs = runReads[index];
    if (runs?.isSuccess === true) runsById.set(definition.id, runs.data.items);
    const schedule = scheduleReads[index];
    if (schedule?.isSuccess === true) scheduleById.set(definition.id, schedule.data);
  });

  const rows = all.filter(
    (definition) =>
      matchesSearch(definition, search) &&
      matchesBoardFilter(filter, runsById.get(definition.id)?.[0]),
  );

  const runsRead = runReads.every((read) => read.isSuccess);
  const neverRun = [...runsById.values()].filter((runs) => runs.length === 0).length;

  const openWizard = (): void => {
    setWizardOpen(true);
  };

  if (identity.isSuccess && !mayRead) {
    return (
      <div className="lq-rc lq-stack">
        <PageHeader
          title={t('reconciliation.title')}
          lede={t('reconciliation.description')}
          help={t('help.feature.reconciliation')}
        />
        <EmptyState
          title={t('reconciliation.forbidden.title')}
          guidance={t('reconciliation.forbidden.body')}
        />
      </div>
    );
  }

  /* Which connection a side lives on, by name. A connection this tenant cannot list reads as not
     reported rather than as its id: a bare UUID in the cell a steward reads first is the defect this
     codebase has recorded four times. */
  const nameOf = (id: string): string =>
    connectionName.get(id) ?? t('reconciliation.connection.unknown');

  return (
    <div className="lq-rc lq-stack">
      <PageHeader
        title={t('reconciliation.title')}
        lede={t('reconciliation.description')}
        help={t('help.feature.reconciliation')}
        actions={
          <div className="lq-rcb__head">
            {definitions.isSuccess ? (
              <span className="lq-rcb__meta">
                {t('reconciliation.meta.definitions', { count: all.length })}
                {runsRead && looked.length === all.length
                  ? ` · ${t('reconciliation.meta.neverRun', { count: neverRun })}`
                  : null}
              </span>
            ) : null}
            {mayAuthor ? (
              <Button tone="primary" onClick={openWizard}>
                {t('reconciliation.newDefinition')}
              </Button>
            ) : (
              /* Offered and withheld read differently. An impossible action and an unpermitted one
                 must not look alike. */
              <span
                className="lq-gated"
                title={t('reconciliation.newDefinition.why', {
                  permission: CREATE_RECONCILIATION_PERMISSION,
                })}
              >
                {t('reconciliation.newDefinition.gated')}
              </span>
            )}
          </div>
        }
      />

      <div className="lq-rc__toolbar">
        <input
          className="lq-rc__search"
          type="search"
          value={search}
          placeholder={t('reconciliation.search.placeholder')}
          aria-label={t('reconciliation.search.label')}
          onChange={(event) => {
            setSearch(event.target.value);
          }}
        />
        <SegmentedFilter
          label={t('reconciliation.filter.label')}
          value={filter}
          options={BOARD_FILTERS.map((each) => ({
            id: each,
            label: t(`reconciliation.filter.${each}`),
          }))}
          onChange={setFilter}
        />
      </div>

      {/* On the wire since the `page()` helper gained it: a capped list that looks complete is the
          defect, not the cap. */}
      {definitions.isSuccess && definitions.data.truncated && (
        <div className="lq-band lq-band--warn">
          <div className="lq-band__title">{t('reconciliation.truncated.title')}</div>
          <div className="lq-band__body">{t('reconciliation.truncated.board')}</div>
        </div>
      )}

      {definitions.isPending && <SkeletonRows rows={4} columns={6} />}

      {definitions.isError && (
        <Alert tone="error" title={t('reconciliation.loadFailed')}>
          {definitions.error.message}
        </Alert>
      )}

      {definitions.isSuccess ? (
        <Card flush>
          <DataTable
            caption={t('reconciliation.caption')}
            columns={[
              {
                key: 'name',
                label: t('reconciliation.column.name'),
                help: t('help.term.reconciliationDefinition'),
              },
              { key: 'compares', label: t('reconciliation.column.compares') },
              { key: 'lastRun', label: t('reconciliation.column.lastRun') },
              { key: 'breaks', label: t('reconciliation.column.breaks') },
              { key: 'schedule', label: t('reconciliation.column.schedule') },
              { key: 'open', label: '', actions: true },
            ]}
          >
            {rows.map((definition) => (
              <tr
                key={definition.id}
                className="lq-rc__row lq-rcb__row"
                onClick={() => {
                  navigate(definitionPath(definition.id));
                }}
              >
                <td>
                  <strong className="lq-rc__name">{definition.name}</strong>
                  <div className="lq-rcb__sub">
                    {definition.is_cross_connection &&
                    definition.right_connection_id !== null
                      ? t('reconciliation.connections.cross', {
                          left: nameOf(definition.connection_id),
                          right: nameOf(definition.right_connection_id),
                        })
                      : nameOf(definition.connection_id)}
                    {definition.is_cross_connection
                      ? ` · ${t('reconciliation.byDigest')}`
                      : null}
                  </div>
                </td>
                <td className="lq-rcb__compares">
                  <div className="lq-mono">
                    <DatasetKindIcon
                      connectionId={definition.connection_id}
                      qualifiedName={definition.left.qualified_name}
                    />
                    {definition.left.qualified_name}
                  </div>
                  <div className="lq-rcb__right lq-mono">
                    <span aria-hidden="true">↔ </span>
                    <DatasetKindIcon
                      connectionId={
                        definition.right_connection_id ?? definition.connection_id
                      }
                      qualifiedName={definition.right.qualified_name}
                    />
                    {definition.right.qualified_name}
                  </div>
                </td>
                <td className="lq-nowrap">
                  <LastRunCell runs={runsById.get(definition.id)} />
                </td>
                <td>
                  <BreaksCell runs={runsById.get(definition.id)} />
                </td>
                <td>
                  <ScheduleCell reading={scheduleById.get(definition.id)} />
                </td>
                <td className="lq-table__actions">
                  <Link
                    className="lq-btn lq-btn--secondary lq-btn--compact"
                    to={definitionPath(definition.id)}
                    onClick={(event) => {
                      event.stopPropagation();
                    }}
                  >
                    {t('reconciliation.showRuns')}
                  </Link>
                </td>
              </tr>
            ))}
          </DataTable>
          {all.length === 0 ? (
            <div className="lq-rcb__empty">
              <p className="lq-rcb__emptyTitle">{t('reconciliation.empty.title')}</p>
              <p className="lq-rcb__emptyBody">{t('reconciliation.empty.body')}</p>
              {mayAuthor ? (
                <Button tone="primary" onClick={openWizard}>
                  {t('reconciliation.newDefinition')}
                </Button>
              ) : null}
            </div>
          ) : rows.length === 0 ? (
            /* A filter that matches nothing is not an empty estate: the definitions are still
                 there, and the sentence says the filter is what came back empty. */
            <p className="lq-rcb__noMatch">{t('reconciliation.noMatches.title')}</p>
          ) : null}
        </Card>
      ) : null}

      {all.length > MAX_DETAIL_LOOKUPS && (
        <p className="lq-note">
          {t('reconciliation.definitions.capped', {
            cap: MAX_DETAIL_LOOKUPS,
            total: all.length,
          })}
        </p>
      )}

      <NewDefinitionWizard
        open={wizardOpen}
        onClose={() => {
          setWizardOpen(false);
        }}
        connections={(connections.data?.items ?? []).map((connection) => ({
          id: connection.id,
          label: connection.name,
        }))}
        datasets={[...(datasetNames.data?.byId.values() ?? [])].sort()}
        existing={all}
      />
    </div>
  );
}
